// ConsentModal.js
import React, {useState} from "react";
import {
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    MenuItem,
    Divider,
    Box,
    List,
    ListItemButton,
    ListItemText,
    Alert,
    CircularProgress,
    Typography,
    Checkbox,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SignatureCanvas from "react-signature-canvas";
import { useAuth } from "../components/TokenHandler";
import InformedConsentModal from "../consent/InformedConsentModal";
import EducationalMaterialModal from "../consent/eductional-material-modal";
import NoticeOfPrivacyPracticesModal from "../consent/NoticeOfPrivacyPracticesModal";

// -------------------------
// Helpers
// -------------------------
const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

const getPhoneLengthMessage = (digits) => {
    const count = digits.length;

    if (count === 0) {
        return "";
    }

    if (count < 10) {
        const missing = 10 - count;
        return `Phone number has ${count} digit${count === 1 ? "" : "s"}. It needs exactly 10 digits. Add ${missing} more digit${missing === 1 ? "" : "s"}.`;
    }

    if (count > 10) {
        const extra = count - 10;
        return `Phone number has ${count} digits. It needs exactly 10 digits. Remove ${extra} digit${extra === 1 ? "" : "s"}.`;
    }

    return "";
};


const pickFirst = (...vals) => {
    for (const v of vals) {
        if (v === undefined || v === null) continue;
        const s = String(v).trim();
        if (s) return s;
    }
    return "";
};

const safeFileName = (s) =>
    String(s || "")
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .slice(0, 120) || "consent";

function formatDateOnly(d) {
    if (!d) return null;
    if (typeof d === "string") return d.slice(0, 10);
    try {
        return new Date(d).toISOString().slice(0, 10);
    } catch {
        return String(d).slice(0, 10);
    }
}

// Trim signature canvas without trim-canvas dependency
function getTrimmedDataURL(canvas) {
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    const img = ctx.getImageData(0, 0, width, height);
    const data = img.data;

    let top = null,
        bottom = null,
        left = null,
        right = null;

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            const alpha = data[i + 3];
            if (alpha > 0) {
                if (top === null) top = y;
                bottom = y;
                if (left === null || x < left) left = x;
                if (right === null || x > right) right = x;
            }
        }
    }

    if (top === null) return null;

    const pad = 10;
    top = Math.max(0, top - pad);
    left = Math.max(0, left - pad);
    bottom = Math.min(height - 1, bottom + pad);
    right = Math.min(width - 1, right + pad);

    const trimW = right - left + 1;
    const trimH = bottom - top + 1;

    const trimmed = document.createElement("canvas");
    trimmed.width = trimW;
    trimmed.height = trimH;

    const tctx = trimmed.getContext("2d");
    tctx.putImageData(ctx.getImageData(left, top, trimW, trimH), 0, 0);

    return trimmed.toDataURL("image/png");
}

// -------------------------
// Constants
// -------------------------
const OPT_OUT_REASONS = ["Deceased", "Facility", "Hospice", "Insurance", "POA", "Verbal","Self POA","Landline"];
const CONSENT_METHODS = ["Verbal", "Verbal / POA", "Facility", "Facility / General"];

// Verbal: accept non-mobile; only block invalid.
const VERBAL_REQUIRE_MOBILE = false;

const getExistingProxyFromContext = (ctx) => {
    const latest = ctx?.consent_latest || ctx?.patient?.consent_latest || null;

    return (
        ctx?.previousProxy ||
        ctx?.patient?.previousProxy ||
        latest?.proxy ||
        null
    );
};

const ConsentModal = ({ isOpen, onClose, onSuccess, mode = "obtain", context = null }) => {
    console.log(context)
    const { keycloak: kc, givenName, familyName, email } = useAuth();

    // -----------------------------
    // Patient/context normalization
    // -----------------------------
    const rawPatient = context?.patient || null;

    const patientInfo = React.useMemo(() => {
        if (!rawPatient) {
            return {
                firstName: "",
                lastName: "",
                email: "",
                mrn: "",
                phoneNumber: "",
                dob: "",
                location: "",
                internalPatientId: "",
                patientStatus: "",
            };
        }

        return {
            firstName: pickFirst(rawPatient.firstName, rawPatient.first_name),
            lastName: pickFirst(rawPatient.lastName, rawPatient.last_name),
            email: pickFirst(rawPatient.email, rawPatient.patient_email),
            mrn: pickFirst(rawPatient.mrn, rawPatient.mrn_id, rawPatient.patient_mrn),
            phoneNumber: pickFirst(
                rawPatient.phoneNumber,
                rawPatient.phone_number,
                rawPatient.patient_phone,
                rawPatient.patient_phone_number
            ),
            dob: pickFirst(rawPatient.dob),
            location: pickFirst(rawPatient.location),
            internalPatientId: pickFirst(rawPatient.patient_id, rawPatient.patient_unique_identifier),
            patientStatus: pickFirst(rawPatient.status),
        };
    }, [rawPatient]);

    const facilityName = React.useMemo(
        () => pickFirst(context?.facility_name, context?.facilityName, context?.careascend?.facility_name),
        [context]
    );

    const providerNumber = React.useMemo(
        () => pickFirst(context?.provider_number, context?.providerNumber, context?.careascend?.facility_number),
        [context]
    );

    const providerNumberForValidation = React.useMemo(() => {
        return pickFirst(
            providerNumber,
            context?.provider_number,
            context?.providerNumber,
            context?.patient?.provider_number,
            context?.patient?.providerNumber
        );
    }, [providerNumber, context]);

    const showMissingProviderNumberWarning =
        mode === "obtain" && !providerNumberForValidation;

    const existingProxy = React.useMemo(() => getExistingProxyFromContext(context), [context]);


    // -----------------------------
    // State
    // -----------------------------
    const [isProxy, setIsProxy] = React.useState(false);
    const [patientOpt, setPatientOpt] = React.useState(""); // optin | optout | ""
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    // Obtain signature
    const [signatureMethod, setSignatureMethod] = React.useState(""); // electronic | paper
    const [isSignatureModalOpen, setIsSignatureModalOpen] = React.useState(false);
    const sigRef = React.useRef(null);
    const [signatureDataUrl, setSignatureDataUrl] = React.useState("");
    const [paperSignatureFile, setPaperSignatureFile] = React.useState(null);


    // Upload admission
    const [admissionFile, setAdmissionFile] = React.useState(null);
    const [admissionNotes, setAdmissionNotes] = React.useState("");
    const [uploadFacilityGeneral, setUploadFacilityGeneral] = React.useState(false);
    const [uploadEffectiveDate, setUploadEffectiveDate] = React.useState(() => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    });

    // Verbal flow
    const [verbalOptOutReason, setVerbalOptOutReason] = React.useState("");
    const [verbalConsentMethod, setVerbalConsentMethod] = React.useState("");
    const [verbalMethod, setVerbalMethod] = React.useState("phone"); // kept for payload

    const [verbalDateTime, setVerbalDateTime] = React.useState(() => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    });

    const verbalDateTimeForSubmit =
        mode === "verbal" && verbalDateTime
            ? `${verbalDateTime}T12:00:00`
            : null;

    // Proxy fields (obtain proxy + verbal POA)
    const [proxyFirstName, setProxyFirstName] = React.useState("");
    const [proxyLastName, setProxyLastName] = React.useState("");
    const [proxyEmail, setProxyEmail] = React.useState("");
    const [proxyPhone, setProxyPhone] = React.useState("");
    const [proxyRelationship, setProxyRelationship] = React.useState("");


    // -----------------------------
    // Line type checks (obtain proxy vs verbal contact)
    // -----------------------------
    const [lineCheck, setLineCheck] = React.useState({
        status: "idle", // idle | checking | ok | bad | error
        message: "",
        response: null,
    });

    const [verbalLineCheck, setVerbalLineCheck] = React.useState({
        status: "idle",
        message: "",
        response: null,
    });

    const debounceRef = React.useRef(null);
    const lastCheckedRef = React.useRef("");

    const verbalDebounceRef = React.useRef(null);
    const verbalLastCheckedRef = React.useRef("");

    // Informed Consent preview modal
    const [openInformedConsent, setOpenInformedConsent] = React.useState(false);
    const [openEdu, setOpenEdu] = useState(false);
    const [openNpp, setOpenNpp] = useState(false);

    // Reset on open/mode change
    React.useEffect(() => {
        if (!isOpen) return;

        setIsProxy(false);
        setPatientOpt("");


        setSignatureMethod("");
        setIsSignatureModalOpen(false);
        setSignatureDataUrl("");
        setPaperSignatureFile(null);

        setAdmissionFile(null);
        setAdmissionNotes("");
        setUploadFacilityGeneral(false);
        const d2 = new Date();
        const pad2 = (n) => String(n).padStart(2, "0");
        setUploadEffectiveDate(`${d2.getFullYear()}-${pad2(d2.getMonth() + 1)}-${pad2(d2.getDate())}`);

        setVerbalOptOutReason("");
        setVerbalConsentMethod("");
        setVerbalMethod("phone");

        setProxyFirstName("");
        setProxyLastName("");
        setProxyEmail("");
        setProxyPhone("");
        setProxyRelationship("");

        setLineCheck({ status: "idle", message: "", response: null });
        lastCheckedRef.current = "";

        setVerbalLineCheck({ status: "idle", message: "", response: null });
        verbalLastCheckedRef.current = "";

        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        setVerbalDateTime(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);

    }, [isOpen, mode]);


    React.useEffect(() => {
        if (!isOpen) return;
        if (!["verbal", "upload"].includes(mode)) return;

        const proxy = getExistingProxyFromContext(context);

        if (!proxy) {
            return;
        }

        const phoneFromProxy = pickFirst(
            proxy?.mobile_phone,
            proxy?.landline,
            proxy?.phone,
            proxy?.phone_number
        );

        // Match the Initiated-bucket behavior for any flow that carries
        // existing proxy information.
        if (mode === "verbal") {
            setVerbalConsentMethod("Verbal / POA");
        }

        if (mode === "upload") {
            setIsProxy(true);
        }

        setProxyFirstName(pickFirst(proxy?.first_name, proxy?.firstName));
        setProxyLastName(pickFirst(proxy?.last_name, proxy?.lastName));
        setProxyEmail(pickFirst(proxy?.email));
        setProxyPhone(digitsOnly(phoneFromProxy));
        setProxyRelationship(pickFirst(proxy?.relationship));
    }, [isOpen, mode, context]);


    // -----------------------------
    // Line type check function (shared)
    // -----------------------------
    const doLineTypeCheck = React.useCallback(
        async ({ tenDigits, setState }) => {
            if (!kc?.token) {
                setState({
                    status: "error",
                    message: "Missing auth token. Cannot verify phone line type.",
                    response: null,
                });
                return;
            }

            setState({
                status: "checking",
                message: "Checking phone line type…",
                response: null,
            });

            try {
                const resp = await fetch(`${process.env.REACT_APP_API_URL}/consent/line-type`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ phone: `+1${tenDigits}` }),
                });

                const data = await resp.json().catch(() => null);

                if (!resp.ok || !data) {
                    setState({
                        status: "error",
                        message: "Line-type check failed.",
                        response: data,
                    });
                    return;
                }

                const isMobile = String(data?.lineType || "").toLowerCase() === "mobile" || data?.isCell === true;

                if (data?.valid && isMobile) {
                    setState({
                        status: "ok",
                        message: `Mobile number confirmed (${data?.nationalFormat || `+1${tenDigits}`}).`,
                        response: data,
                    });
                } else if (data?.valid) {
                    setState({
                        status: "bad",
                        message: `This number is not mobile (line type: ${data?.lineType || "unknown"}).`,
                        response: data,
                    });
                } else {
                    setState({
                        status: "bad",
                        message: "Phone number is not valid.",
                        response: data,
                    });
                }
            } catch {
                setState({
                    status: "error",
                    message: "Unable to check phone line type (network/server error).",
                    response: null,
                });
            }
        },
        [kc?.token]
    );

// Upload: trigger check when proxy/designee phone is entered
    React.useEffect(() => {
        if (!isOpen) return;
        if (mode !== "upload") return;
        if (!isProxy) return;

        const digits = digitsOnly(proxyPhone);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (!digits) {
            setLineCheck({ status: "idle", message: "", response: null });
            lastCheckedRef.current = "";
            return;
        }

        debounceRef.current = setTimeout(() => {
            if (digits.length !== 10) {
                setLineCheck({
                    status: "bad",
                    message: getPhoneLengthMessage(digits),
                    response: {
                        valid: false,
                        reason: "invalid_length",
                        digitCount: digits.length,
                        expectedDigits: 10,
                    },
                });
                lastCheckedRef.current = "";
                return;
            }

            if (lastCheckedRef.current === digits) return;

            lastCheckedRef.current = digits;
            doLineTypeCheck({ tenDigits: digits, setState: setLineCheck });
        }, 900);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [proxyPhone, isOpen, isProxy, mode, doLineTypeCheck]);

// Obtain: trigger line check only for proxy
    React.useEffect(() => {
        if (!isOpen) return;
        if (!isProxy) return;
        if (mode !== "obtain") return;

        const digits = digitsOnly(proxyPhone);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (!digits) {
            setLineCheck({ status: "idle", message: "", response: null });
            lastCheckedRef.current = "";
            return;
        }

        debounceRef.current = setTimeout(() => {
            if (digits.length !== 10) {
                setLineCheck({
                    status: "bad",
                    message: getPhoneLengthMessage(digits),
                    response: {
                        valid: false,
                        reason: "invalid_length",
                        digitCount: digits.length,
                        expectedDigits: 10,
                    },
                });
                lastCheckedRef.current = "";
                return;
            }

            if (lastCheckedRef.current === digits) return;

            lastCheckedRef.current = digits;
            doLineTypeCheck({ tenDigits: digits, setState: setLineCheck });
        }, 900);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [proxyPhone, isOpen, isProxy, mode, doLineTypeCheck]);

// Verbal: trigger check when contact info exists

    const verbalNeedsProxyDetails =
        mode === "verbal" && verbalConsentMethod === "Verbal / POA";

    const verbalContactPhoneRaw = React.useMemo(() => {
        if (verbalNeedsProxyDetails) {
            return proxyPhone;
        }

        return patientInfo.phoneNumber || "";
    }, [verbalNeedsProxyDetails, proxyPhone, patientInfo.phoneNumber]);

    React.useEffect(() => {
        if (!isOpen) return;
        if (mode !== "verbal") return;
        if (!verbalConsentMethod) return;

        const digits = digitsOnly(verbalContactPhoneRaw);

        if (verbalDebounceRef.current) clearTimeout(verbalDebounceRef.current);

        if (!digits) {
            setVerbalLineCheck({ status: "idle", message: "", response: null });
            verbalLastCheckedRef.current = "";
            return;
        }

        verbalDebounceRef.current = setTimeout(() => {
            if (digits.length !== 10) {
                setVerbalLineCheck({
                    status: "bad",
                    message: getPhoneLengthMessage(digits),
                    response: {
                        valid: false,
                        reason: "invalid_length",
                        digitCount: digits.length,
                        expectedDigits: 10,
                    },
                });
                verbalLastCheckedRef.current = "";
                return;
            }

            if (verbalLastCheckedRef.current === digits) return;

            verbalLastCheckedRef.current = digits;
            doLineTypeCheck({ tenDigits: digits, setState: setVerbalLineCheck });
        }, 900);

        return () => {
            if (verbalDebounceRef.current) clearTimeout(verbalDebounceRef.current);
        };
    }, [isOpen, mode, verbalConsentMethod, verbalContactPhoneRaw, doLineTypeCheck]);

    // -----------------------------
    // Signature handlers
    // -----------------------------
    const saveDrawnSignature = () => {
        if (!sigRef.current) return;
        if (sigRef.current.isEmpty()) {
            alert("Please draw a signature first.");
            return;
        }

        const canvas = sigRef.current.getCanvas();
        const dataUrl = getTrimmedDataURL(canvas) || canvas.toDataURL("image/png");
        setSignatureDataUrl(dataUrl);
        setIsSignatureModalOpen(false);
    };



    const clearSignature = () => {
        sigRef.current?.clear();
        setSignatureDataUrl("");
    };


    // -----------------------------
    // Backend PDF creation helpers
    // -----------------------------
    const mapPatientOptForPdf = React.useCallback((value) => {
        if (value === "Opted in" || value === "POA Opted in") return "optin";
        if (value === "Opted out" || value === "POA Opted out") return "optout";
        return "";
    }, []);

    const buildPdfPatient = React.useCallback(() => {
        const patientName = `${patientInfo.firstName} ${patientInfo.lastName}`.trim();

        return {
            name: patientName || "(name of patient)",
            firstName: patientInfo.firstName || "",
            lastName: patientInfo.lastName || "",
            dob: patientInfo.dob || "",
            facility_name: facilityName || "",
            provider_number: providerNumber || "",
        };
    }, [patientInfo, facilityName, providerNumber]);

    const buildPdfProxy = React.useCallback(() => {
        const shouldUseProxy =
            (mode === "obtain" && isProxy) ||
            (mode === "verbal" && verbalConsentMethod === "Verbal / POA");

        if (!shouldUseProxy) return null;

        return {
            name:
                `${pickFirst(proxyFirstName, existingProxy?.first_name)} ${pickFirst(
                    proxyLastName,
                    existingProxy?.last_name
                )}`.trim(),
            firstName: pickFirst(proxyFirstName, existingProxy?.first_name),
            lastName: pickFirst(proxyLastName, existingProxy?.last_name),
            phone: pickFirst(
                proxyPhone ? `+1${digitsOnly(proxyPhone)}` : "",
                existingProxy?.mobile_phone,
                existingProxy?.landline
            ),
            email: pickFirst(proxyEmail, existingProxy?.email),
            relationship: pickFirst(proxyRelationship, existingProxy?.relationship),
        };
    }, [
        mode,
        isProxy,
        verbalConsentMethod,
        proxyFirstName,
        proxyLastName,
        proxyPhone,
        proxyEmail,
        proxyRelationship,
        existingProxy,
    ]);

    const createInformedConsentPdfFromBackend = React.useCallback(
        async ({ pdfPatientOpt, signedOn, signatureSrc }) => {
            const apiBase = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

            if (!apiBase) {
                throw new Error("Missing REACT_APP_API_URL.");
            }

            if (!pdfPatientOpt) {
                throw new Error("Missing opt-in / opt-out selection for PDF.");
            }

            const resp = await fetch(`${apiBase}/consent/pdf/informed-consent`, {
                method: "POST",
                headers: {
                    ...(kc?.token ? { Authorization: `Bearer ${kc.token}` } : {}),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    patientOpt: pdfPatientOpt,
                    signedOn,
                    patient: buildPdfPatient(),
                    proxy: buildPdfProxy(),
                    signatureDataUrl: signatureSrc || "",
                    consentMethod:
                        mode === "obtain"
                            ? "electronic"
                            : mode === "upload"
                                ? "paper"
                                : mode === "verbal"
                                    ? verbalConsentMethod || ""
                                    : "",
                }),
            });

            if (!resp.ok) {
                const text = await resp.text();
                let message = "Failed to create informed consent PDF.";

                try {
                    const parsed = JSON.parse(text);
                    message = parsed?.error || message;
                } catch {
                    message = text || message;
                }

                throw new Error(message);
            }

            const blob = await resp.blob();

            const patientName = `${patientInfo.firstName} ${patientInfo.lastName}`.trim();
            const dateOnly = formatDateOnly(signedOn) || new Date().toISOString().slice(0, 10);

            return new File(
                [blob],
                `Informed-Consent-${safeFileName(patientName)}-${safeFileName(dateOnly)}.pdf`,
                { type: "application/pdf" }
            );
        },
        [
            kc?.token,
            buildPdfPatient,
            buildPdfProxy,
            mode,
            verbalConsentMethod,
            patientInfo.firstName,
            patientInfo.lastName,
        ]
    );


    const getLatestConsentFromContext = React.useCallback(() => {
        return context?.consent_latest || context?.patient?.consent_latest || null;
    }, [context]);

    const isInitiatedLatestConsent = React.useCallback(() => {
        const latest = getLatestConsentFromContext();

        return (
            mode === "obtain" &&
            String(latest?.consent || "").trim().toLowerCase() === "initiated" &&
            (latest?.active === true || latest?.active === "true" || latest?.active === 1)
        );
    }, [mode, getLatestConsentFromContext]);

    const hasProxyOnLatestConsent = React.useCallback(() => {
        const latest = getLatestConsentFromContext();

        return Boolean(
            latest?.proxy_id ||
            latest?.proxy?.id ||
            latest?.proxy
        );
    }, [getLatestConsentFromContext]);

    const archiveInitiatedConsentIfNeeded = React.useCallback(async ({ createdBy }) => {
        if (!isInitiatedLatestConsent()) {
            return {
                skipped: true,
                archived: false,
                attemptsStopped: false,
            };
        }

        const latest = getLatestConsentFromContext();
        const consentId = String(latest?.id || "").trim();
        const patientId = pickFirst(
            latest?.patient_id,
            context?.patient?.patient_id,
            context?.patient?.internalPatientId,
            context?.patient?.patient_unique_identifier
        );

        if (!consentId) {
            throw new Error("Cannot archive initiated consent because the latest consent record is missing an id.");
        }

        if (!patientId) {
            throw new Error("Cannot stop messaging attempts because patient_id is missing.");
        }

        if (!kc?.token) {
            throw new Error("Missing token. Cannot archive initiated consent record.");
        }

        const nowIso = new Date().toISOString();
        const patientHasProxy = hasProxyOnLatestConsent();

        const resetPayload = {
            id: consentId,
            mode: "erroneous",
            reason: "archived consent",
            patient_has_proxy: patientHasProxy,
            created_by: {
                date: nowIso,
                email: createdBy?.email || email || "",
                action: "archived - consent reversal",
                givenName: createdBy?.givenName || givenName || "",
                familyName: createdBy?.familyName || familyName || "",
            },
        };

        const resetUrl = `${process.env.REACT_APP_API_URL}/consent/reset_consent`;

        const resetResp = await fetch(resetUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${kc.token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(resetPayload),
        });

        const resetData = await resetResp.json().catch(() => ({}));

        if (!resetResp.ok) {
            throw new Error(
                resetData?.message ||
                resetData?.error ||
                `Unable to archive initiated consent record: ${resetResp.status} ${resetResp.statusText}`
            );
        }

        const stopAttemptsPayload = {
            patient_id: String(patientId).trim(),
        };

        const stopAttemptsUrl = `${process.env.REACT_APP_API_URL}/consent/stop_attempts`;

        const stopAttemptsResp = await fetch(stopAttemptsUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${kc.token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(stopAttemptsPayload),
        });

        const stopAttemptsData = await stopAttemptsResp.json().catch(() => ({}));

        if (!stopAttemptsResp.ok) {
            throw new Error(
                stopAttemptsData?.message ||
                stopAttemptsData?.error ||
                `Unable to stop messaging attempts: ${stopAttemptsResp.status} ${stopAttemptsResp.statusText}`
            );
        }

        return {
            skipped: false,
            archived: true,
            attemptsStopped: true,
            resetPayload,
            resetResponse: resetData,
            stopAttemptsPayload,
            stopAttemptsResponse: stopAttemptsData,
        };
    }, [
        isInitiatedLatestConsent,
        getLatestConsentFromContext,
        hasProxyOnLatestConsent,
        kc?.token,
        email,
        givenName,
        familyName,
        context,
    ]);

    const proxyDetailsComplete = React.useMemo(() => {
        return (
            proxyFirstName.trim() &&
            proxyLastName.trim() &&
            digitsOnly(proxyPhone).length === 10 &&
            proxyRelationship.trim()
        );
    }, [proxyFirstName, proxyLastName, proxyPhone, proxyRelationship]);

    const isFormComplete = React.useMemo(() => {
        // Missing provider number blocks obtain mode
        if (showMissingProviderNumberWarning) return false;

        // Do not allow save while phone checks are running
        if (lineCheck.status === "checking" || verbalLineCheck.status === "checking") {
            return false;
        }

        // -----------------------------
        // OBTAIN MODE
        // -----------------------------
        if (mode === "obtain") {
            // User must choose proxy yes/no implicitly exists through isProxy,
            // but the flow always defaults to "no", so validate based on branch.

            if (isProxy) {
                return Boolean(proxyDetailsComplete);
            }

            if (!patientOpt) return false;

            if (!signatureMethod) return false;

            if (signatureMethod === "electronic" && !signatureDataUrl) {
                return false;
            }

            if (signatureMethod === "paper" && !paperSignatureFile) {
                return false;
            }

            return true;
        }

        // -----------------------------
        // UPLOAD MODE
        // -----------------------------
        if (mode === "upload") {
            if (!patientOpt) return false;

            if (!uploadEffectiveDate) return false;

            if (!admissionFile) return false;

            // Since you said all fields must be filled out for all modes,
            // require proxy fields when Proxy/Designee is selected.
            if (isProxy && !proxyDetailsComplete) {
                return false;
            }

            return true;
        }

        // -----------------------------
        // VERBAL MODE
        // -----------------------------
        if (mode === "verbal") {
            if (!verbalConsentMethod) return false;

            if (!patientOpt) return false;

            if (!verbalDateTime) return false;

            const isOptOut =
                patientOpt === "Opted out" ||
                patientOpt === "POA Opted out";

            if (isOptOut && !verbalOptOutReason) {
                return false;
            }

            if (verbalConsentMethod === "Verbal / POA" && !proxyDetailsComplete) {
                return false;
            }

            return true;
        }

        return false;
    }, [
        mode,
        showMissingProviderNumberWarning,
        isProxy,
        patientOpt,
        signatureMethod,
        signatureDataUrl,
        paperSignatureFile,
        uploadEffectiveDate,
        admissionFile,
        verbalConsentMethod,
        verbalDateTime,
        verbalOptOutReason,
        proxyDetailsComplete,
        lineCheck.status,
        verbalLineCheck.status,
    ]);


    // -----------------------------
    // Save handler
    // -----------------------------
    const handleSave = async () => {
        if (isSubmitting || !isFormComplete) return;

        setIsSubmitting(true);

        try {
            const createdBy = {
                givenName: pickFirst(givenName, context?.created_by?.givenName),
                familyName: pickFirst(familyName, context?.created_by?.familyName),
                email: pickFirst(email, context?.created_by?.email),
            };

            const providerNumberForValidation = pickFirst(
                providerNumber,
                context?.provider_number,
                context?.providerNumber,
                context?.patient?.provider_number,
                context?.patient?.providerNumber
            );

            if (mode === "obtain" && !providerNumberForValidation) {
                alert("Provider number is missing. Please select a facility with a provider number before obtaining consent.");
                return;
            }

            // -----------------------------
            // OBTAIN validations
            // -----------------------------
            if (mode === "obtain" && isProxy) {
                if (lineCheck.status === "checking") return;

                const invalid =
                    lineCheck.status === "bad" &&
                    lineCheck.response?.valid === false;

                if (invalid) {
                    setLineCheck((prev) => ({
                        ...prev,
                        status: "bad",
                        message: "Phone number is not valid. Please correct it before saving.",
                    }));
                    return;
                }

                if (
                    !proxyFirstName.trim() ||
                    !proxyLastName.trim() ||
                    digitsOnly(proxyPhone).length !== 10 ||
                    !proxyRelationship.trim()
                ) {
                    alert("Please complete all Proxy/Designee fields.");
                    return;
                }
            }

            if (mode === "obtain" && !isProxy) {
                if (!patientOpt) {
                    alert("Please select an opt-in or opt-out choice.");
                    return;
                }

                if (!signatureMethod) {
                    alert("Please select Paper or Electronic for signature.");
                    return;
                }

                if (signatureMethod === "electronic" && !signatureDataUrl) {
                    alert("Please draw and save the electronic signature.");
                    return;
                }

                if (signatureMethod === "paper" && !paperSignatureFile) {
                    alert("Please choose the signed paper consent file.");
                    return;
                }
            }

            // -----------------------------
            // UPLOAD validations
            // -----------------------------
            if (mode === "upload") {
                if (!patientOpt) {
                    alert("Please select Opt In or Opt Out.");
                    return;
                }

                if (!uploadEffectiveDate) {
                    alert("Please select an effective date.");
                    return;
                }

                if (!admissionFile) {
                    alert("Please choose a file to upload.");
                    return;
                }

                if (isProxy) {
                    const digits = digitsOnly(proxyPhone);

                    if (digits.length === 10 && lineCheck.status === "checking") {
                        return;
                    }

                    if (
                        !proxyFirstName.trim() ||
                        !proxyLastName.trim() ||
                        digits.length !== 10 ||
                        !proxyRelationship.trim()
                    ) {
                        alert("Please complete all Proxy/Designee fields.");
                        return;
                    }
                }
            }

            // -----------------------------
            // VERBAL validations
            // -----------------------------
            if (mode === "verbal") {
                if (!verbalConsentMethod) {
                    alert("Please select how the patient consented.");
                    return;
                }

                if (!patientOpt) {
                    alert("Please select an opt-in or opt-out choice.");
                    return;
                }

                if (
                    (patientOpt === "Opted out" || patientOpt === "POA Opted out") &&
                    !verbalOptOutReason
                ) {
                    alert("Please select a reason to opt out.");
                    return;
                }

                if (verbalConsentMethod === "Verbal / POA") {
                    const digits = digitsOnly(proxyPhone);

                    if (
                        !proxyFirstName.trim() ||
                        !proxyLastName.trim() ||
                        digits.length !== 10 ||
                        !proxyRelationship.trim()
                    ) {
                        alert("Please complete all Proxy/Designee fields.");
                        return;
                    }
                }

                if (!verbalDateTime) {
                    alert("Please select a verbal date.");
                    return;
                }

                const contactDigits = digitsOnly(verbalContactPhoneRaw);

                if (contactDigits.length === 10) {
                    if (verbalLineCheck.status === "checking") return;

                    const invalid =
                        verbalLineCheck.status === "bad" &&
                        verbalLineCheck.response?.valid === false;

                    if (invalid) {
                        alert("Phone number is not valid. Please correct it before saving.");
                        return;
                    }

                    const notMobileButValid =
                        verbalLineCheck.status === "bad" &&
                        verbalLineCheck.response?.valid === true;

                    if (VERBAL_REQUIRE_MOBILE && notMobileButValid) {
                        alert("Phone number must be a mobile line for verbal consent contact information.");
                        return;
                    }
                }
            }

            const patientName = `${patientInfo.firstName} ${patientInfo.lastName}`.trim();
            const dateOnly = formatDateOnly(new Date()) || new Date().toISOString().slice(0, 10);

            // -----------------------------
            // Build payload
            // -----------------------------
            const payload = {
                mode,
                context,
                facilityName,
                providerNumber,
                created_by: createdBy,
                patientRaw: rawPatient,
                patient: patientInfo,

                patient_unique_id: pickFirst(rawPatient?.patient_unique_id, rawPatient?.patientUniqueId),
                facility_name: facilityName || pickFirst(context?.facility_name, context?.facilityName),
                provider_number: providerNumber || pickFirst(context?.provider_number, context?.providerNumber),

                obtain:
                    mode === "obtain"
                        ? {
                            isProxy,
                            patientOpt,
                            signatureMethod,
                            signature: signatureMethod === "electronic" ? signatureDataUrl : null,
                            paperSignatureFileName:
                                signatureMethod === "paper"
                                    ? paperSignatureFile?.name || null
                                    : null,
                            proxy: isProxy
                                ? {
                                    firstName: proxyFirstName,
                                    lastName: proxyLastName,
                                    email: proxyEmail,
                                    phone: proxyPhone ? `+1${digitsOnly(proxyPhone)}` : "",
                                    relationship: proxyRelationship,
                                    lineTypeResponse: lineCheck.response,
                                    sms_capable: lineCheck.status === "ok",
                                    line_type: String(lineCheck.response?.lineType || ""),
                                }
                                : null,
                        }
                        : null,

                upload:
                    mode === "upload"
                        ? {
                            isProxy,
                            facility_general: uploadFacilityGeneral,
                            patientOpt,
                            effective_date: uploadEffectiveDate || null,
                            fileName: admissionFile?.name || null,
                            notes: admissionNotes || "",
                            contact: isProxy
                                ? {
                                    type: "proxy",
                                    phone: proxyPhone ? `+1${digitsOnly(proxyPhone)}` : "",
                                    lineTypeResponse: lineCheck.response,
                                    sms_capable: lineCheck.status === "ok",
                                    line_type: String(lineCheck.response?.lineType || ""),
                                }
                                : {
                                    type: "patient",
                                    phone: patientInfo.phoneNumber
                                        ? `+1${digitsOnly(patientInfo.phoneNumber)}`
                                        : "",
                                    lineTypeResponse: null,
                                    sms_capable: false,
                                    line_type: "",
                                },
                            proxy: isProxy
                                ? {
                                    firstName: proxyFirstName,
                                    lastName: proxyLastName,
                                    email: proxyEmail,
                                    phone: proxyPhone ? `+1${digitsOnly(proxyPhone)}` : "",
                                    relationship: proxyRelationship,
                                }
                                : null,
                        }
                        : null,

                verbal:
                    mode === "verbal"
                        ? {
                            patientOpt,
                            optOutReason:
                                patientOpt === "Opted out" || patientOpt === "POA Opted out"
                                    ? verbalOptOutReason
                                    : null,
                            consentMethod: verbalConsentMethod || null,
                            verbalMethod,
                            verbalDateTime: verbalDateTimeForSubmit,

                            contact: {
                                type: verbalConsentMethod === "Verbal / POA" ? "proxy" : "patient",
                                phone: verbalContactPhoneRaw
                                    ? `+1${digitsOnly(verbalContactPhoneRaw)}`
                                    : "",
                                lineTypeResponse: verbalLineCheck.response,
                                sms_capable: verbalLineCheck.status === "ok",
                                line_type: String(verbalLineCheck.response?.lineType || ""),
                            },

                            proxy:
                                verbalConsentMethod === "Verbal / POA"
                                    ? {
                                        firstName: proxyFirstName,
                                        lastName: proxyLastName,
                                        email: proxyEmail,
                                        phone: proxyPhone ? `+1${digitsOnly(proxyPhone)}` : "",
                                        relationship: proxyRelationship,
                                    }
                                    : null,
                        }
                        : null,
            };

            const url = `${process.env.REACT_APP_API_URL}/consent/insert_consent`;

            // -----------------------------
            // Decide file-to-send
            // -----------------------------
            let fileToSend = null;

            if (mode === "upload" && admissionFile) {
                fileToSend = admissionFile;
            }

            if (mode === "obtain" && !isProxy && patientOpt && signatureMethod === "electronic") {
                fileToSend = await createInformedConsentPdfFromBackend({
                    pdfPatientOpt: mapPatientOptForPdf(patientOpt),
                    signedOn: dateOnly,
                    signatureSrc: signatureDataUrl || "",
                });
            }

            if (mode === "verbal" && patientOpt) {
                fileToSend = await createInformedConsentPdfFromBackend({
                    pdfPatientOpt: mapPatientOptForPdf(patientOpt),
                    signedOn: verbalDateTime || dateOnly,
                    signatureSrc: "",
                });
            }

            const requestType = fileToSend ? "multipart/form-data" : "application/json";

            const debugInfo = {
                url,
                mode,
                requestType,
                hasFile: Boolean(fileToSend),
                file: fileToSend
                    ? {
                        name: fileToSend.name,
                        type: fileToSend.type,
                        size: fileToSend.size,
                    }
                    : null,
                payloadObject: payload,
                payloadJson: JSON.stringify(payload, null, 2),
            };

            window.__LAST_INSERT_CONSENT__ = debugInfo;

            console.groupCollapsed(
                `%cinsert_consent request [${mode}]`,
                "color:#6a00ff;font-weight:bold"
            );
            console.log("URL:", url);
            console.log("Request type:", requestType);
            console.log("Mode:", mode);
            console.log("File attached:", debugInfo.file);
            console.log("Payload object:", payload);
            console.log("Payload JSON:", debugInfo.payloadJson);
            console.groupEnd();

            // If this is obtain mode and the latest active consent is "initiated",
            // archive the initiated record first. Only continue to insert_consent if this succeeds.
            if (mode === "obtain") {
                await archiveInitiatedConsentIfNeeded({ createdBy });
            }

            let resp;
            let data;

            if (fileToSend) {
                const formData = new FormData();
                formData.append("file", fileToSend);
                formData.append("payload", JSON.stringify(payload));

                console.log("FormData payload field:", JSON.stringify(payload, null, 2));

                resp = await fetch(url, {
                    method: "POST",
                    headers: {
                        ...(kc?.token ? { Authorization: `Bearer ${kc.token}` } : {}),
                    },
                    body: formData,
                });

                data = await resp.json().catch(() => null);
            } else {
                console.log("JSON request body:", JSON.stringify(payload, null, 2));

                resp = await fetch(url, {
                    method: "POST",
                    headers: {
                        ...(kc?.token ? { Authorization: `Bearer ${kc.token}` } : {}),
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                });

                data = await resp.json().catch(() => null);
            }

            if (!resp.ok) {
                console.error("insert_consent failed:", resp.status, data);
                alert(data?.error || "Unable to save consent. Please try again.");
                return;
            }

            if (typeof onSuccess === "function") {
                await onSuccess(data);
            }

            onClose();
        } catch (e) {
            console.error("insert_consent error:", e);
            alert(e?.message || "Network/server error while saving consent.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // -----------------------------
    // UI pieces
    // -----------------------------
    const modalTitle =
        mode === "upload" ? "Upload New Admission" : mode === "verbal" ? "Verbal Consent" : "Obtain Consent";

    const RenderFacilityInfo = () => (
        <div style={styles.information}>
            <p>
                <strong>A partner of TAPESTRY HEALTH</strong>
            </p>
            <p style={{ marginTop: 6 }}>
                <strong>Facility:</strong> {facilityName || "N/A"}{" "}
                {providerNumber ? (
                    <>
                        <span style={{ opacity: 0.7 }}>•</span> <strong>Provider #:</strong> {providerNumber}
                    </>
                ) : null}
            </p>
            <p>
                <Button variant="contained" onClick={() => setOpenEdu(true)}>
                    Educational Material
                </Button>
            </p>
        </div>
    );

    const RenderProxyToggle = ({ uploadMode = false } = {}) => (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <p style={{ marginBottom: 6 }}>
                <strong>
                    {uploadMode
                        ? "Was the consent completed by a Proxy/Designee?"
                        : "Does the consent need to be completed by a Proxy/Designee?"}
                </strong>
            </p>

            <RadioGroup
                row
                name={uploadMode ? "uploadProxyDesignee" : "proxyDesignee"}
                value={isProxy ? "yes" : "no"}
                onChange={(e) => setIsProxy(e.target.value === "yes")}
            >
                <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
            </RadioGroup>
        </div>
    );




    const RenderProxyDetails = ({ enforceMobileCheck = false, useVerbalState = false }) => {
        const lc = useVerbalState ? verbalLineCheck : lineCheck;



        return (
            <div style={{ marginTop: 10 }}>
                <p style={{ textAlign: "center" }}>
                    <strong>Proxy/Designee Details</strong>
                </p>

                <TextField
                    label="Proxy / Designee First Name *"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={proxyFirstName}
                    onChange={(e) => setProxyFirstName(e.target.value)}
                />

                <TextField
                    label="Proxy / Designee Last Name *"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={proxyLastName}
                    onChange={(e) => setProxyLastName(e.target.value)}
                />

                <TextField
                    label="Proxy / Designee Email"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={proxyEmail}
                    onChange={(e) => setProxyEmail(e.target.value)}
                />

                <TextField
                    label="Proxy / Designee Phone Number *"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={proxyPhone}
                    onChange={(e) => setProxyPhone(digitsOnly(e.target.value))}
                    helperText={enforceMobileCheck ? "Enter 10 digits to verify line type." : "Enter 10 digits."}
                    inputProps={{ inputMode: "numeric" }}
                    onBlur={() => {
                        if (!enforceMobileCheck) return;
                        const digits = digitsOnly(proxyPhone);
                        const lastRef = useVerbalState ? verbalLastCheckedRef : lastCheckedRef;
                        if (digits.length === 10 && lastRef.current !== digits) {
                            lastRef.current = digits;
                            doLineTypeCheck({ tenDigits: digits, setState: useVerbalState ? setVerbalLineCheck : setLineCheck });
                        }
                    }}
                />

                {enforceMobileCheck && (
                    <div style={{ marginTop: 8 }}>
                        {lc.status === "checking" && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2">Checking phone line type…</Typography>
                            </Box>
                        )}
                        {lc.status === "ok" && <Alert severity="success">{lc.message}</Alert>}
                        {lc.status === "bad" && <Alert severity={VERBAL_REQUIRE_MOBILE && useVerbalState ? "error" : "warning"}>{lc.message}</Alert>}
                        {lc.status === "error" && <Alert severity="error">{lc.message}</Alert>}
                    </div>
                )}

                <TextField
                    select
                    label="Proxy / Designee Relationship"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={proxyRelationship}
                    onChange={(e) => setProxyRelationship(e.target.value)}
                >
                    <MenuItem value="MPOA">Medical Power of Attorney (MPOA)</MenuItem>
                    <MenuItem value="HCP">Medical Health Care Proxy (HCP)</MenuItem>
                    <MenuItem value="Conservator">Conservator</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                </TextField>
            </div>
        );
    };

    const RenderPatientOpt = ({ isVerbal = false }) => {
        const isPoaVerbal = isVerbal && verbalConsentMethod === "Verbal / POA";

        return (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <p style={{ marginBottom: 6 }}>
                    <strong>{isPoaVerbal ? "Is POA ready to Opt in/out?" : "Is Patient ready to Opt in/out?"}</strong>
                </p>

                <RadioGroup
                    row
                    name="PatientOpt"
                    value={patientOpt}
                    onChange={(e) => setPatientOpt(e.target.value)}
                >
                    <FormControlLabel
                        value={isPoaVerbal ? "POA Opted in" : "Opted in"}
                        control={<Radio color="primary" />}
                        label={isPoaVerbal ? "POA Opt In" : "Opt In"}
                    />
                    <FormControlLabel
                        value={isPoaVerbal ? "POA Opted out" : "Opted out"}
                        control={<Radio color="primary" />}
                        label={isPoaVerbal ? "POA Opt Out" : "Opt Out"}
                    />
                </RadioGroup>
            </div>
        );
    };





    // -----------------------------
    // OBTAIN
    // -----------------------------
    const RenderObtainConsent = () => (
        <div style={{ padding: "0 15px 15px" }}>
            <RenderFacilityInfo />
            <Divider style={{ margin: "10px 0" }} />

            {showMissingProviderNumberWarning && (
                <Alert
                    severity="warning"
                    icon={<WarningAmberIcon />}
                    sx={{ mb: 2 }}
                >
                    Provider number is missing. Consent cannot be obtained until a provider number is available for this facility.
                </Alert>
            )}

            <RenderProxyToggle />

            {isProxy ? (
                <>{RenderProxyDetails({ enforceMobileCheck: true, useVerbalState: false })}</>
            ) : (
                <>
                    {RenderPatientOpt({ isVerbal: false })}

                    {!isProxy && patientOpt && (
                        <div style={{ marginTop: 15, padding: 10, border: "1px solid #ccc", borderRadius: 5 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    style={{ marginTop: 15 }}
                                    onClick={() => setOpenInformedConsent(true)}
                                    disabled={!patientOpt}
                                >
                                    View Informed Consent
                                </Button>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    style={{ marginTop: 15 }}
                                    onClick={() => setOpenNpp(true)}
                                >
                                    View Notice of Privacy Practices
                                </Button>
                            </Box>

                            <p style={{ textAlign: "center", marginTop: 16 }}>
                                <strong>Signature</strong>
                            </p>

                            <RadioGroup
                                row
                                name="signatureMethod"
                                value={signatureMethod}
                                onChange={(e) => {
                                    const m = e.target.value;
                                    setSignatureMethod(m);
                                    if (m === "electronic") setIsSignatureModalOpen(true);
                                    else setIsSignatureModalOpen(false);
                                }}
                                style={{ justifyContent: "center" }}
                            >
                                <FormControlLabel value="electronic" control={<Radio color="primary" />} label="Electronic" />

                            </RadioGroup>

                            {signatureMethod === "paper" && (
                                <div style={{ marginTop: 10, padding: 12, border: "1px solid #ddd", borderRadius: 6 }}>
                                    <p style={{ marginTop: 0 }}>
                                        Upload the signed paper consent (PDF or image). <strong>(Not wired yet)</strong>
                                    </p>
                                    <Button variant="contained" component="label">
                                        Choose file
                                        <input
                                            type="file"
                                            hidden
                                            accept="application/pdf,image/*"
                                            onChange={(e) => setPaperSignatureFile(e.target.files?.[0] || null)}
                                        />
                                    </Button>

                                    <div style={{ marginTop: 12, textAlign: "left" }}>
                                        <strong>Selected:</strong> {paperSignatureFile ? paperSignatureFile.name : "None"}
                                    </div>
                                </div>
                            )}

                            {signatureMethod === "electronic" && isSignatureModalOpen && (
                                <div style={{ marginTop: 15 }}>
                                    <div style={styles.modalOverlay}>
                                        <div style={styles.signatureModal}>
                                            <h3>Draw Your Signature</h3>

                                            <div style={styles.signatureBox}>
                                                <SignatureCanvas
                                                    ref={sigRef}
                                                    penColor="black"
                                                    canvasProps={{
                                                        width: 480,
                                                        height: 180,
                                                        style: { width: "100%", height: "100%", borderRadius: 6 },
                                                    }}
                                                />
                                            </div>

                                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 2, flexWrap: "wrap" }}>
                                                <Button variant="outlined" onClick={clearSignature}>
                                                    Clear
                                                </Button>
                                                <Button variant="contained" onClick={saveDrawnSignature}>
                                                    Save Signature
                                                </Button>
                                                <Button variant="text" onClick={() => setIsSignatureModalOpen(false)}>
                                                    Close
                                                </Button>
                                            </Box>

                                            {signatureDataUrl && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        Saved preview:
                                                    </Typography>
                                                    <img
                                                        src={signatureDataUrl}
                                                        alt="Saved signature"
                                                        style={{ maxWidth: "100%", border: "1px solid #ddd", borderRadius: 6 }}
                                                    />
                                                </Box>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {signatureMethod === "electronic" && !isSignatureModalOpen && signatureDataUrl && (
                                <Box sx={{ mt: 2, p: 1.5, border: "1px solid #ddd", borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Signature saved:
                                    </Typography>
                                    <img
                                        src={signatureDataUrl}
                                        alt="Saved signature"
                                        style={{ maxWidth: "100%", border: "1px solid #eee", borderRadius: 6 }}
                                    />
                                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                                        <Button size="small" onClick={() => setIsSignatureModalOpen(true)}>
                                            Edit
                                        </Button>
                                        <Button size="small" color="error" onClick={() => setSignatureDataUrl("")}>
                                            Remove
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );

    // -----------------------------
    // UPLOAD
    // -----------------------------
    const RenderUploadAdmission = () => (
        <div style={{ padding: "0 15px 15px" }}>
            <div style={{ marginTop: 12 }}>
                <RenderFacilityInfo />
            </div>

            <Divider style={{ margin: "10px 0" }} />

            <RenderProxyToggle uploadMode />

            <Box sx={{ mt: 2, p: 1.5, border: "1px solid #ddd", borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                    Facility / General?
                </Typography>

                <RadioGroup
                    row
                    name="uploadFacilityGeneral"
                    value={uploadFacilityGeneral ? "yes" : "no"}
                    onChange={(e) => setUploadFacilityGeneral(e.target.value === "yes")}
                >
                    <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                </RadioGroup>
            </Box>

            <div style={{ marginTop: 10 }}>
                <p style={{ marginBottom: 6, fontWeight: 600 }}>
                    {isProxy ? "Is Proxy/Designee ready to Opt in/out?" : "Is Patient ready to Opt in/out?"}
                </p>

                <List
                    disablePadding
                    sx={{
                        border: "1px solid #d0d0d0",
                        borderRadius: 1,
                        overflow: "hidden",
                        backgroundColor: "#fff",
                    }}
                >
                    <ListItemButton
                        onClick={() => {
                            setPatientOpt(isProxy ? "POA Opted in" : "Opted in");
                        }}
                        sx={{ py: 1.5 }}
                    >
                        <ListItemText primary={isProxy ? "Proxy / Designee Opt In" : "Opt In"} />
                        {(patientOpt === "Opted in" || patientOpt === "POA Opted in") && (
                            <CheckIcon sx={{ color: "#6a00ff" }} />
                        )}
                    </ListItemButton>

                    <Divider />

                    <ListItemButton
                        onClick={() => {
                            setPatientOpt(isProxy ? "POA Opted out" : "Opted out");

                        }}
                        sx={{ py: 1.5 }}
                    >
                        <ListItemText primary={isProxy ? "Proxy / Designee Opt Out" : "Opt Out"} />
                        {(patientOpt === "Opted out" || patientOpt === "POA Opted out") && (
                            <CheckIcon sx={{ color: "#6a00ff" }} />
                        )}
                    </ListItemButton>
                </List>
            </div>

            {isProxy && (
                <Box sx={{ mt: 2, p: 1.5, border: "1px solid #ddd", borderRadius: 2 }}>
                    {RenderProxyDetails({ enforceMobileCheck: true, useVerbalState: false })}
                    <Alert severity="info" sx={{ mt: 1 }}>
                        Proxy/Designee fields are optional in upload mode because this information is coming from a paper document.
                    </Alert>
                </Box>
            )}

            {patientOpt && (
                <div style={{ marginTop: 10, padding: 12, border: "1px solid #ddd", borderRadius: 6 }}>
                    <p style={{ marginTop: 0 }}>
                        Upload an admission file (PDF, image, or spreadsheet). Once uploaded, we’ll attach it to this patient record.
                    </p>

                    <TextField
                        label="Effective Date"
                        type="date"
                        fullWidth
                        margin="normal"
                        value={uploadEffectiveDate}
                        onChange={(e) => setUploadEffectiveDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />

                    <Button variant="contained" component="label">
                        Choose file
                        <input
                            type="file"
                            hidden
                            onChange={(e) => setAdmissionFile(e.target.files?.[0] || null)}
                        />
                    </Button>

                    <div style={{ marginTop: 12, textAlign: "left" }}>
                        <strong>Selected:</strong> {admissionFile ? admissionFile.name : "None"}
                    </div>


                </div>
            )}
        </div>
    );

    // -----------------------------
    // VERBAL
    // -----------------------------
    const RenderVerbalConsent = () => {
        const needsProxyDetails = verbalConsentMethod === "Verbal / POA";

        return (
            <div style={{ padding: "0 15px 15px" }}>
                <RenderFacilityInfo />
                <Divider style={{ margin: "10px 0" }} />

                <Box sx={{ mt: 2, p: 1.5, border: "1px solid #ddd", borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>How has the patient consented</Typography>

                    <TextField
                        select
                        fullWidth
                        label="Consent method"
                        value={verbalConsentMethod}
                        onChange={(e) => {
                            const v = e.target.value;
                            setVerbalConsentMethod(v);
                            setPatientOpt("");
                            setVerbalOptOutReason("");
                            setVerbalLineCheck({ status: "idle", message: "", response: null });
                            verbalLastCheckedRef.current = "";

                            if (v !== "Verbal / POA") {
                                setProxyFirstName("");
                                setProxyLastName("");
                                setProxyEmail("");
                                setProxyPhone("");
                                setProxyRelationship("");
                            }
                        }}
                    >
                        <MenuItem value="">(select)</MenuItem>
                        {CONSENT_METHODS.map((m) => (
                            <MenuItem key={m} value={m}>
                                {m}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                {verbalConsentMethod && (
                    <Box sx={{ mt: 2, p: 1.5, border: "1px solid #ddd", borderRadius: 2 }}>
                        {RenderPatientOpt({ isVerbal: true })}
                    </Box>
                )}

                {(patientOpt === "Opted out" || patientOpt === "POA Opted out") && (
                    <Box sx={{ mt: 2, p: 1.5, border: "1px solid #ddd", borderRadius: 2 }}>
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Please select reason to opt out</Typography>

                        <TextField
                            select
                            fullWidth
                            label="Opt-out reason"
                            value={verbalOptOutReason}
                            onChange={(e) => setVerbalOptOutReason(e.target.value)}
                        >
                            <MenuItem value="">(select)</MenuItem>
                            {OPT_OUT_REASONS.map((r) => (
                                <MenuItem key={r} value={r}>
                                    {r}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                )}

                {verbalConsentMethod && (
                    <Box sx={{ mt: 2, p: 1.5, border: "1px solid #ddd", borderRadius: 2 }}>
                        {needsProxyDetails ? (
                            <Box sx={{ mt: 2 }}>
                                {RenderProxyDetails({ enforceMobileCheck: true, useVerbalState: true })}
                            </Box>
                        ) : (
                            <Box sx={{ mt: 2 }}>
                                <div style={{ marginTop: 8 }}>
                                    {verbalLineCheck.status === "checking" && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <CircularProgress size={16} />
                                            <Typography variant="body2">Checking phone line type…</Typography>
                                        </Box>
                                    )}
                                    {verbalLineCheck.status === "ok" && <Alert severity="success">{verbalLineCheck.message}</Alert>}
                                    {verbalLineCheck.status === "bad" && (
                                        <Alert severity={VERBAL_REQUIRE_MOBILE ? "error" : "warning"}>
                                            {verbalLineCheck.message}
                                        </Alert>
                                    )}
                                    {verbalLineCheck.status === "error" && <Alert severity="error">{verbalLineCheck.message}</Alert>}
                                </div>
                            </Box>
                        )}

                        <Box sx={{ mt: 2 }}>
                            <TextField
                                label="Verbal Date"
                                type="date"
                                fullWidth
                                value={verbalDateTime}
                                onChange={(e) => setVerbalDateTime(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>

                        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                            <Button
                                variant="contained"
                                onClick={() => setOpenInformedConsent(true)}
                                disabled={!patientOpt}
                            >
                                View Informed Consent
                            </Button>
                        </Box>
                    </Box>
                )}
            </div>
        );
    };

    // -----------------------------
    // Render
    // -----------------------------
    if (!isOpen) return null;

    const patientName = `${patientInfo.firstName} ${patientInfo.lastName}`.trim();
    const today = new Date().toISOString().slice(0, 10);

    const effectiveDate =
        mode === "verbal"
            ? verbalDateTime || today
            : today;

    // Data passed to preview modal
    const informedPatient = {
        name: patientName || "(name of patient)",
        dob: patientInfo.dob || "",
        phone: patientInfo.phoneNumber || "",
        email: patientInfo.email || "",
        facility_name: facilityName || "",
        provider_number: providerNumber || "",
    };

    const informedProxy =
        (mode === "obtain" && isProxy) || (mode === "verbal" && verbalConsentMethod === "Verbal / POA")
            ? {
                name:
                    `${pickFirst(proxyFirstName, existingProxy?.first_name)} ${pickFirst(
                        proxyLastName,
                        existingProxy?.last_name
                    )}`.trim(),
                phone: pickFirst(
                    proxyPhone ? `+1${digitsOnly(proxyPhone)}` : "",
                    existingProxy?.mobile_phone,
                    existingProxy?.landline
                ),
                email: pickFirst(proxyEmail, existingProxy?.email),
                relationship: pickFirst(proxyRelationship, existingProxy?.relationship),
            }
            : null;

    const informedConsentMethod =
        mode === "obtain"
            ? "electronic"
            : mode === "upload"
                ? "paper"
                : mode === "verbal"
                    ? verbalConsentMethod || ""
                    : "";

    // Signature ONLY for obtain electronic; verbal sends none
    const signatureSrcForPreview = mode === "obtain" && signatureMethod === "electronic" ? signatureDataUrl : "";

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <span style={styles.title}>{modalTitle}</span>
                    <span style={styles.closeButton} onClick={onClose}>
            &times;
          </span>
                </div>

                <div style={styles.patientInfo}>
                    <p>
                        <strong>{patientName || "Patient"}</strong>
                    </p>

                    {patientInfo.email && <p>{patientInfo.email}</p>}

                    {facilityName && (
                        <p>
                            <strong>Facility:</strong> {facilityName}
                        </p>
                    )}

                    {providerNumber && (
                        <p>
                            <strong>Provider #:</strong> {providerNumber}
                        </p>
                    )}

                    <p>
                        <strong>MRN:</strong> {patientInfo.mrn || "N/A"}
                    </p>

                    <p>
                        <strong>DOB:</strong> {patientInfo.dob || "N/A"}
                    </p>

                    {patientInfo.location && (
                        <p>
                            <strong>Location:</strong> {patientInfo.location}
                        </p>
                    )}

                    {patientInfo.internalPatientId && (
                        <p>
                            <strong>Internal Patient ID:</strong> {patientInfo.internalPatientId}
                        </p>
                    )}
                </div>

                <Divider />

                {mode === "obtain" && RenderObtainConsent()}
                {mode === "upload" && RenderUploadAdmission()}
                {mode === "verbal" && RenderVerbalConsent()}

                <div style={styles.buttonContainer}>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        style={styles.saveButton}
                        disabled={isSubmitting || !isFormComplete}
                        startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {isSubmitting ? "Saving…" : "Save"}
                    </Button>
                    <Button onClick={onClose} variant="outlined" color="secondary" style={styles.cancelButton}>
                        Cancel
                    </Button>
                </div>

                {/* Real preview modal (your existing InformedConsentModal) */}
                <InformedConsentModal
                    open={openInformedConsent}
                    onClose={() => setOpenInformedConsent(false)}
                    patientOpt={
                        patientOpt === "Opted in" || patientOpt === "POA Opted in"
                            ? "optin"
                            : patientOpt === "Opted out" || patientOpt === "POA Opted out"
                                ? "optout"
                                : ""
                    }
                    signedOn={effectiveDate}
                    patient={informedPatient}
                    proxy={informedProxy}
                    signatureSrc={signatureSrcForPreview}
                    autoGeneratePdf={false}
                    consentMethod={informedConsentMethod}
                />

                <NoticeOfPrivacyPracticesModal open={openNpp} onClose={() => setOpenNpp(false)} />
                <EducationalMaterialModal
                    open={openEdu}
                    onClose={() => setOpenEdu(false)}
                    facilityName={facilityName}
                />
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
    },
    modal: {
        backgroundColor: "white",
        borderRadius: "5px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
        width: "900px",
        maxHeight: "85vh",
        overflowY: "auto",
        fontFamily: "'Roboto', sans-serif",
        padding: "2px",
        position: "relative",
    },
    header: {
        backgroundColor: "#007bff",
        color: "white",
        padding: "10px 15px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    title: {
        fontWeight: "bold",
        fontSize: "16px",
    },
    closeButton: {
        fontSize: "20px",
        fontWeight: "bold",
        cursor: "pointer",
    },
    patientInfo: {
        backgroundColor: "#f7f8fa",
        padding: "15px",
        borderRadius: "5px",
        margin: "10px",
        lineHeight: "1.2",
        textAlign: "left",
    },
    information: {
        padding: "10px",
        textAlign: "center",
        fontSize: "14px",
        lineHeight: "1.5",
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "space-between",
        padding: "15px",
    },
    saveButton: {
        marginRight: "10px",
    },
    cancelButton: {
        marginLeft: "10px",
    },
    modalOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1200,
    },
    signatureModal: {
        backgroundColor: "white",
        borderRadius: "6px",
        padding: "20px",
        width: "520px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
        textAlign: "center",
    },
    signatureBox: {
        height: "180px",
        border: "1px dashed #aaa",
        borderRadius: "6px",
        marginTop: "10px",
        overflow: "hidden",
    },
};

export default ConsentModal;
